export enum WaypointType {
  FoodSource = 'food_source',
  Camera = 'camera',
  AccessPoint = 'access_point',
  WaterSource = 'water_source',
  GameTrail = 'game_trail',
  BeddingArea = 'bedding_area',
  Blind = 'blind',
  ObservationPoint = 'observation_point',
  Other = 'other',
}

// ---------------------------------------------------------------------------
// GeoJSON geometry types — inline to avoid a full geojson package dependency
// ---------------------------------------------------------------------------

export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: [number, number][][]; // [lng, lat] pairs per GeoJSON spec
}

export interface GeoJSONLineString {
  type: 'LineString';
  coordinates: [number, number][]; // [lng, lat] pairs
}

// ---------------------------------------------------------------------------
// Food source subtypes
// ---------------------------------------------------------------------------

export type FoodSourceType = 'food_plot' | 'fruit_tree' | 'mineral_site' | 'feeder' | 'nuts' | 'berries' | 'other';

interface FoodSourceMetadataBase {
  type: 'food_source';
  foodSourceType: FoodSourceType;
  boundaryGeoJSON?: GeoJSONPolygon;       // Optional boundary drawing for ALL subtypes
  plotSizeAcresCalculated?: number;        // Derived from boundary polygon via Shoelace formula
}

export interface FoodPlotSubtype extends FoodSourceMetadataBase {
  foodSourceType: 'food_plot';
  plantedSpecies: string[];               // e.g., ["brassica_mix", "winter_rye", "clover"]
  targetSeasonDraw?: 'early_bow' | 'peak_rut' | 'late_season' | 'winter' | 'year_round';
  soilTestDate?: string;
  soilPH?: number;
  lastPlantedDate?: string;
  lastFertilizedDate?: string;
}

export interface FruitTreeSubtype extends FoodSourceMetadataBase {
  foodSourceType: 'fruit_tree';
  treeSpecies?: 'apple' | 'pear' | 'crabapple' | 'persimmon' | 'plum' | 'cherry' | 'other';
  numberOfTrees?: number;
  plantedDate?: string;
  maturity?: 'seedling' | 'young' | 'producing' | 'mature';
  dropSeason?: 'early_fall' | 'mid_fall' | 'late_fall' | 'winter';
  fenced?: boolean;
}

export interface MineralSiteSubtype extends FoodSourceMetadataBase {
  foodSourceType: 'mineral_site';
  mineralType?: string;                   // e.g., "salt block", "Trophy Rock", "loose mineral", "liquid"
  lastRefreshedDate?: string;
  refreshIntervalDays?: number;
}

export interface FeederSubtype extends FoodSourceMetadataBase {
  foodSourceType: 'feeder';
  feederType?: 'gravity' | 'spin' | 'trough' | 'broadcast' | 'other';
  capacityLbs?: number;
  feedType?: string;                      // e.g., "corn", "protein pellets", "rice bran", "mixed"
  lastFilledDate?: string;
  fillIntervalDays?: number;
  timerSchedule?: string;                 // e.g., "6am and 5pm, 5 seconds"
  batteryType?: string;                   // e.g., "6V", "solar"
  lastBatteryChange?: string;
}

export interface NutsSubtype extends FoodSourceMetadataBase {
  foodSourceType: 'nuts';
  nutSpecies?: 'oak_white' | 'oak_red' | 'hickory' | 'chestnut' | 'beech' | 'other';
  numberOfTrees?: number;
  mastProduction?: 'poor' | 'fair' | 'good' | 'excellent' | 'unknown';
  dropSeason?: 'early_fall' | 'mid_fall' | 'late_fall';
  notes?: string;
}

export interface BerriesSubtype extends FoodSourceMetadataBase {
  foodSourceType: 'berries';
  berrySpecies?: 'blackberry' | 'raspberry' | 'honeysuckle' | 'sumac' | 'dogwood' | 'pokeberry' | 'greenbrier' | 'other';
  areaSize?: 'patch' | 'thicket' | 'scattered';
  peakSeason?: 'summer' | 'early_fall' | 'mid_fall' | 'late_fall';
  plantedOrNatural?: 'planted' | 'natural';
  notes?: string;
}

export interface OtherFoodSourceSubtype extends FoodSourceMetadataBase {
  foodSourceType: 'other';
  description?: string;
  seasonAvailable?: 'spring' | 'summer' | 'fall' | 'winter' | 'year_round';
  notes?: string;
}

export type FoodSourceMetadata =
  | FoodPlotSubtype
  | FruitTreeSubtype
  | MineralSiteSubtype
  | FeederSubtype
  | NutsSubtype
  | BerriesSubtype
  | OtherFoodSourceSubtype;

export type CloudPlatform =
  | 'spypoint'
  | 'reconyx'
  | 'tactacam'
  | 'moultrie'
  | 'browning'
  | 'stealth_cam'
  | 'bushnell'
  | 'covert'
  | 'other'
  | 'none';

export interface CameraMetadata {
  type: 'camera';
  cameraModel?: string;
  cameraType: 'sd_card' | 'cellular';
  facing?: string; // Compass direction
  cloudPlatform?: CloudPlatform;
  cloudCameraId?: string;
}

export interface WaterSourceMetadata {
  type: 'water_source';
  waterType: 'pond' | 'creek' | 'spring' | 'waterhole_constructed' | 'other';
  seasonal: boolean;
  lastCheckedDate?: string;
}

export interface BeddingAreaMetadata {
  type: 'bedding_area';
  beddingType: 'natural' | 'created_hinge_cut' | 'created_screen' | 'other';
  coverDensity: 'light' | 'moderate' | 'heavy';
  primarySpeciesUsing?: string;
}

export interface BlindMetadata {
  type: 'blind';
  blindType?: 'box' | 'ground' | 'elevated' | 'hub' | 'ladder' | 'hang_on' | 'observation_tower' | 'other';
  capacity?: number;
  hasSeat?: boolean;
  hasHeater?: boolean;
  idealWindDirections?: string[]; // e.g. ['N', 'NE', 'E']
  bestTimeOfDay?: 'morning' | 'evening' | 'all_day';
}

export interface GameTrailMetadata {
  type: 'game_trail';
  pathGeoJSON?: GeoJSONLineString; // drawn polyline
}

export type WaypointMetadata =
  | FoodSourceMetadata
  | CameraMetadata
  | WaterSourceMetadata
  | BeddingAreaMetadata
  | BlindMetadata
  | GameTrailMetadata
  | { type: 'access_point' | 'observation_point' | 'other' };

// ---------------------------------------------------------------------------
// Waypoint entity
// ---------------------------------------------------------------------------

export interface Waypoint {
  id: string;
  propertyId: string;
  name: string;
  type: WaypointType;
  latitude: number;
  longitude: number;
  notes: string;
  metadata?: WaypointMetadata; // Type-specific fields — required in M6 rework
  visibleOnMap?: boolean; // defaults to true when absent
  createdAt: string;
  updatedAt: string;
}

export type CreateWaypointInput = Omit<Waypoint, 'id' | 'createdAt' | 'updatedAt'>;
export type UpdateWaypointInput = Partial<CreateWaypointInput>;
